import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { environment } from '@env/environment';
import { UrlParserService } from '@app-services/url-parser.service';
import { BehaviorSubject, Observable } from 'rxjs';
import { RealEstateFilter, RealEstateItem } from '@app-model/realEstate';
import { RealEstateFilterOffices, RealEstateWidget, RealStateFilterFeatures } from '@app-model/promotedContent';
import { AvailableIntegration } from '@app-model/integration';
import { EntityType } from '@app/enum/entity-type.enum';
import { ResponsePagination } from '@app-type/default-types';

interface ResponseRealEstateFilterOffices extends ResponsePagination {
	data: RealEstateFilterOffices[];
}

interface ResponseRealStateFilterFeatures extends ResponsePagination {
	data: RealStateFilterFeatures[];
}

interface SearchCountResponse {
	success: boolean;
	message: string;
	totalElements: number;
}

interface PageControl {
	page?: number;
	pageSize?: number;
	sortField?: string;
	sortDirection?: string;
	param?: string;
	value?: string;
}

@Injectable({
	providedIn: 'root'
})
export class RealEstateService {
	private readonly mlsOptionsLoading$$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
	get mlsOptionsLoading(): boolean {
		return this.mlsOptionsLoading$$.getValue();
	}

	private mlsOptionsLoadingCount = 0;

	/**
	 * Increment the loading counter and emit loading=true.
	 */
	mlsLoadingStart(): void {
		this.mlsOptionsLoadingCount = Math.max(0, this.mlsOptionsLoadingCount) + 1;
		if (this.mlsOptionsLoadingCount > 0) {
			this.mlsOptionsLoading$$.next(true);
		}
	}

	/**
	 * Decrement the loading counter and emit loading=false only when count reaches 0.
	 */
	mlsLoadingStop(): void {
		this.mlsOptionsLoadingCount = Math.max(0, this.mlsOptionsLoadingCount - 1);
		if (this.mlsOptionsLoadingCount <= 0) {
			this.mlsOptionsLoadingCount = 0;
			this.mlsOptionsLoading$$.next(false);
		}
	}

	constructor(private http: HttpClient, private urlParser: UrlParserService) { }

	private buildQueryParams(obj?: PageControl, allowedFields?: string[], prefix: string = ''): string {
		if (!obj) {
			return '';
		}

		let params = new HttpParams();

		Object.entries(obj)
			.filter(([key, value]) => allowedFields?.includes(key) && value !== undefined && value !== null && value !== '')
			.forEach(([key, value]) => {
				const paramName = prefix ? `${prefix}.${key}` : key;
				params = params.set(paramName, String(value));
			});

		const queryString = params.toString();
		return queryString ? `?${queryString}` : '';
	}

	private buildFilterParams(filter?: RealEstateFilter, mapPropertyStatusToStatus = false): HttpParams {
		let params = new HttpParams();

		if (!filter) {
			return params;
		}

		Object.entries(filter).forEach(([key, value]) => {
			if (value === undefined || value === null || value === '') {
				return;
			}

			const paramKey = mapPropertyStatusToStatus && key === 'propertyStatus' ? 'status' : key;

			if (Array.isArray(value)) {
				value
					.filter(item => item !== undefined && item !== null && item !== '')
					.forEach(item => {
						params = params.append(paramKey, String(item));
					});
				return;
			}

			params = params.append(paramKey, String(value));
		});

		return params;
	}

	getRealEstateItems(
		entityType: string,
		entityId: string,
		filter?: RealEstateFilter,
		limit?: number
	): Observable<RealEstateItem[]> {
		let params = new HttpParams();

		if (limit) {
			params = params.set('limit', limit.toString());
		}

		if (filter) {
			const filterParams = this.buildFilterParams(filter);
			filterParams.keys().forEach(key => {
				filterParams
					.getAll(key)
					?.forEach(value => {
						params = params.append(key, value);
					});
			});
		}

		return this.http.get<RealEstateItem[]>(`${environment.api}/realEstate/listing/${entityType}/${entityId}`, {
			params
		});
	}
	getWidgets(entityType: string, entityId: string): Observable<RealEstateWidget[]> {
		return this.http.get<RealEstateWidget[]>(`${environment.api}/realEstate/widgets/${entityType}/${entityId}`);
	}

	isFilterEnabled(entityType: string, entityId: string): Observable<boolean> {
		return this.http.get<boolean>(`${environment.api}/realEstate/filter-enabled/${entityType}/${entityId}`);
	}

	getAvailableIntegrations(entityType: EntityType, entityId: string): Observable<AvailableIntegration[]> {
		return this.http.get<AvailableIntegration[]>(
			`${environment.api}/realEstate/available-integrations/${entityType}/${entityId}`
		);
	}

	getFilterFeatures(
		entityType: string,
		entityId: string,
		pageControl?: PageControl,
		name?: string
	): Observable<ResponseRealStateFilterFeatures> {
		const allowedFields = ['page', 'pageSize', 'sortField', 'sortDirection', 'param', 'value'];
		let queryParams = this.buildQueryParams(pageControl, allowedFields, 'pageControl');
		const trimmedName = name?.trim();
		if (trimmedName) {
			queryParams = queryParams
				? `${queryParams}&name=${encodeURIComponent(trimmedName)}`
				: `?name=${encodeURIComponent(trimmedName)}`;
		}

		return this.http.get<ResponseRealStateFilterFeatures>(
			`${environment.api}/realEstate/filterOptions/features/top/${entityType}/${entityId}${queryParams}`
		);
	}

	getFilterFeaturesByCardholder(
		pageControl?: PageControl,
		name?: string,
		slugs?: string[]
	): Observable<ResponseRealStateFilterFeatures> {
		const allowedFields = ['page', 'pageSize', 'sortField', 'sortDirection', 'param', 'value'];
		let params = new HttpParams();

		if (pageControl) {
			Object.entries(pageControl)
				.filter(([key, value]) => allowedFields.includes(key) && value !== undefined && value !== null && value !== '')
				.forEach(([key, value]) => {
					params = params.set(`pageControl.${key}`, String(value));
				});
		}

		const trimmedName = name?.trim();
		if (trimmedName) {
			params = params.set('name', trimmedName);
		}

		slugs
			?.filter(slug => !!slug)
			.forEach(slug => {
				params = params.append('slugs', slug);
			});

		const headers = new HttpHeaders().set('X-MBC-Origin-Path', this.urlParser.getBasePath());
		return this.http.get<ResponseRealStateFilterFeatures>(
			`${environment.api}/open/filter-options/features/by-cardholder`,
			{ headers, params }
		);
	}
	getFilterOffices(
		entityType: string,
		entityId: string,
		pageControl?: PageControl
	): Observable<ResponseRealEstateFilterOffices> {
		const allowedFields = ['page', 'pageSize', 'sortField', 'sortDirection', 'param', 'value'];
		const queryParams = this.buildQueryParams(pageControl, allowedFields, '');

		return this.http.get<ResponseRealEstateFilterOffices>(
			`${environment.api}/realEstate/filterOptions/offices/${entityType}/${entityId}${queryParams}`
		);
	}

	countSearchResults(entityType: string, entityId: string, filter?: RealEstateFilter): Observable<SearchCountResponse> {
		return this.http.get<SearchCountResponse>(`${environment.api}/realEstate/search/count/${entityType}/${entityId}`, {
			params: this.buildFilterParams(filter, true)
		});
	}
}
